import React, { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { BarChart3, Activity } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

function ScoreBar({ score }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-1">
      <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
      <span className="text-[10px] w-6 text-right">{score}</span>
    </div>
  );
}

export function AuctionEvaluationTab({ eventRef }) {
  const [evalData, setEvalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeScenario, setActiveScenario] = useState(null);
  const [dtlSort, setDtlSort] = useState({ field: '', dir: 'asc' });
  const [rankSort, setRankSort] = useState({ field: '', dir: 'asc' });
  const hDtl = (field, dir) => setDtlSort({ field, dir });
  const hRank = (field, dir) => setRankSort({ field, dir });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/api/rfx/events/${eventRef}/evaluate`);
        setEvalData(r.data);
        if (r.data.recommendation) setActiveScenario(r.data.recommendation.id);
      } catch (e) { toast.error(getApiErrorMessage(e, 'Evaluation failed')); }
      setLoading(false);
    };
    load();
  }, [eventRef]);

  if (loading) return <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>;
  if (!evalData || !evalData.total_bids) {
    return (
      <div className="text-center py-12 text-slate-400" data-testid="eval-empty">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No bids received yet. Evaluation will be available once suppliers submit bids.</p>
      </div>
    );
  }

  const { scenarios = [], recommendation, weighted_rankings = [], savings_story, weights, baseline_estimate } = evalData;
  const selected = scenarios.find(s => s.id === activeScenario) || recommendation;

  return (
    <div className="space-y-5 py-2" data-testid="auction-evaluation-tab">
      {savings_story && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5" data-testid="savings-story">
          <div className="flex items-start gap-4">
            <div className="bg-emerald-100 rounded-full p-3"><BarChart3 className="w-6 h-6 text-emerald-700" /></div>
            <div className="flex-1">
              <h3 className="font-bold text-emerald-900 text-lg">{savings_story.savings_pct > 0 ? `${savings_story.savings_pct}% Savings Achieved` : 'Evaluation Complete'}</h3>
              <p className="text-sm text-emerald-800 mt-1">{savings_story.narrative}</p>
              <div className="flex gap-6 mt-3 text-sm">
                <div><span className="text-emerald-600 text-xs">Baseline</span><p className="font-bold">${baseline_estimate?.toLocaleString()}</p></div>
                <div><span className="text-emerald-600 text-xs">Recommended</span><p className="font-bold">${savings_story.recommended_cost?.toLocaleString()}</p></div>
                <div><span className="text-emerald-600 text-xs">Savings</span><p className="font-bold text-emerald-700">${Math.abs(savings_story.savings_amount)?.toLocaleString()}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {evalData.smart_recommendation && (
        <div className="bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-200 rounded-xl p-4" data-testid="smart-rec">
          <div className="flex items-start gap-3">
            <div className="bg-sky-100 rounded-full p-2"><Activity className="w-5 h-5 text-sky-700" /></div>
            <div>
              <h4 className="font-semibold text-sm text-sky-900">Smart Recommendation</h4>
              <p className="text-sm text-sky-800 mt-1">{evalData.smart_recommendation.narrative}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold mb-3">Award Scenarios — Click to Compare</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {scenarios.slice(0, 6).map(s => {
            const isSelected = s.id === activeScenario;
            const isRecommended = recommendation && s.id === recommendation.id;
            return (
              <div key={s.id} onClick={() => setActiveScenario(s.id)}
                className={`border rounded-xl p-4 cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-indigo-500 border-indigo-300 bg-indigo-50' : 'hover:shadow-md hover:border-slate-300'
                }`} data-testid={`scenario-${s.id}`}>
                {isRecommended && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] mb-2">Recommended</Badge>}
                <h4 className="font-semibold text-sm">{s.name}</h4>
                <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-slate-800">${s.total_cost?.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{s.supplier_count} supplier(s)</p>
                  </div>
                  {s.savings_pct !== 0 && (
                    <Badge className={`text-xs ${s.savings_pct > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {s.savings_pct > 0 ? '-' : '+'}{Math.abs(s.savings_pct)}%
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selected?.details && (
        <div className="border rounded-xl p-4 bg-white">
          <h3 className="text-sm font-semibold mb-3">Breakdown: {selected.name}</h3>
          <div className="border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-100 border-b">
                <SortableTableHeader label="Item / Lot" field="description" sortState={dtlSort} onSort={hDtl} />
                <SortableTableHeader label="Qty" field="quantity" sortState={dtlSort} onSort={hDtl} />
                <SortableTableHeader label="Best Price" field="best_price" sortState={dtlSort} onSort={hDtl} className="text-right" />
                <SortableTableHeader label="Line Total" field="line_total" sortState={dtlSort} onSort={hDtl} className="text-right" />
                <SortableTableHeader label="Awarded To" field="supplier" sortState={dtlSort} onSort={hDtl} />
              </tr></thead>
              <tbody>
                {sortData(selected.details, dtlSort.field, dtlSort.dir).map((d, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-3 py-2 font-medium">{d.description || d.lot_name || d.item_id}</td>
                    <td className="px-3 py-2">{d.quantity || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{d.best_price != null ? `$${d.best_price.toLocaleString()}` : d.best_amount != null ? `$${d.best_amount.toLocaleString()}` : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{d.line_total != null ? `$${d.line_total.toLocaleString()}` : d.best_amount != null ? `$${d.best_amount.toLocaleString()}` : '—'}</td>
                    <td className="px-3 py-2">{d.supplier || '—'}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td colSpan={3} className="px-3 py-2 text-right">Total:</td>
                  <td className="px-3 py-2 text-right font-mono text-indigo-700">${selected.total_cost?.toLocaleString()}</td>
                  <td className="px-3 py-2">{selected.suppliers?.join(', ')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="border rounded-xl p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Weighted Supplier Rankings</h3>
          <div className="flex gap-2 text-[10px] text-slate-400">
            <span>Price: {weights?.price}%</span>
            <span>Delivery: {weights?.delivery}%</span>
            <span>Quality: {weights?.quality}%</span>
            <span>ESG: {weights?.esg}%</span>
          </div>
        </div>
        <div className="border rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-100 border-b">
              <SortableTableHeader label="Rank" field="rank" sortState={rankSort} onSort={hRank} className="w-8" />
              <SortableTableHeader label="Supplier" field="supplier_name" sortState={rankSort} onSort={hRank} />
              <SortableTableHeader label="Total Bid" field="total_bid" sortState={rankSort} onSort={hRank} className="text-right" />
              <SortableTableHeader label="Price" field="price_score" sortState={rankSort} onSort={hRank} className="text-center" />
              <SortableTableHeader label="Delivery" field="delivery_score" sortState={rankSort} onSort={hRank} className="text-center" />
              <SortableTableHeader label="Quality" field="quality_score" sortState={rankSort} onSort={hRank} className="text-center" />
              <SortableTableHeader label="ESG" field="esg_score" sortState={rankSort} onSort={hRank} className="text-center" />
              <SortableTableHeader label="Weighted" field="weighted_score" sortState={rankSort} onSort={hRank} className="text-center" />
            </tr></thead>
            <tbody>
              {sortData(weighted_rankings.map((r, idx) => ({...r, rank: idx + 1})), rankSort.field, rankSort.dir).map((r, idx) => (
                <tr key={r.supplier_id} className={`border-b ${idx === 0 ? 'bg-emerald-50' : ''}`}>
                  <td className="px-3 py-2 font-bold text-center">{idx + 1}</td>
                  <td className="px-3 py-2 font-medium">{r.supplier_name}</td>
                  <td className="px-3 py-2 text-right font-mono">${r.total_bid?.toLocaleString()}</td>
                  <td className="px-3 py-2 text-center"><ScoreBar score={r.price_score} /></td>
                  <td className="px-3 py-2 text-center"><ScoreBar score={r.delivery_score} /></td>
                  <td className="px-3 py-2 text-center"><ScoreBar score={r.quality_score} /></td>
                  <td className="px-3 py-2 text-center"><ScoreBar score={r.esg_score} /></td>
                  <td className="px-3 py-2 text-center font-bold text-indigo-700">{r.weighted_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-slate-700">{evalData.total_bids}</p>
          <p className="text-xs text-slate-500">Total Bids</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-slate-700">{evalData.total_suppliers}</p>
          <p className="text-xs text-slate-500">Suppliers</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-slate-700">{scenarios.length}</p>
          <p className="text-xs text-slate-500">Scenarios</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-700">${baseline_estimate?.toLocaleString()}</p>
          <p className="text-xs text-slate-500">Baseline Estimate</p>
        </div>
      </div>
    </div>
  );
}
